/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './ImageColumn';
import { helpRenders } from './ImageColumn_Help';

const ImageColumnManager: ComponentManagerFn<'ImageColumn'> = (
  platform: Platform,
): ComponentManager<'ImageColumn'> => {
  return {
    ...baseComponentManager<'ImageColumn'>(platform, 'ImageColumn'),
    type: 'ImageColumn',
    name: 'Image Column',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnCellClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: ({ props }) => {
          return ['String'];
        },
        helpText: 'The attribute that contains the image URL',
      },
      rounding: {
        type: 'numberInput',
        label: 'Rounding',
      },
    },
    isPropertyVisible: (prop, itemData, { appUid, itemId, pageId }) => {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      return (
        prop !== 'insertable' &&
        prop !== 'updatable' &&
        prop !== 'colorExpression' &&
        prop !== 'filterable' &&
        prop !== 'sortable' &&
        prop !== 'required'
      );
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'image' },
    description: `
    
    The Image Column component is used to display images inside a Data Grid.  
    
    Each cell in this column renders an image using the URL specified in the View Attribute.  
        
    You must specify a View Attribute that points to a valid image URL or a data field that contains an image path.

    Key Features
    - Supports dynamic image rendering directly from the data source.  
    - Can be combined with triggers such as Trigger On Cell Click for interactivity.  
    - Ideal for displaying user profile pictures, product thumbnails, or icons within grid cells.`,
    helpRenders,
  };
};

export default ImageColumnManager;
